import { useSyncExternalStore } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isPersistableQueryKey, useOnlineStatus } from '@sportschool/shared-logic';
import { formatUtc, getDateLocale } from '@/lib/utils';

/**
 * Age of the data on screen: the oldest `dataUpdatedAt` among the active (mounted) cached queries that are part of
 * the offline cache. Returns 0 when nothing is on screen yet. A number, so it is a stable `useSyncExternalStore` snapshot.
 */
function oldestOnScreenDataUpdatedAt(queryClient: QueryClient): number {
  let oldest = 0;
  for (const query of queryClient.getQueryCache().findAll({ type: 'active' })) {
    const updatedAt = query.state.dataUpdatedAt;
    if (query.state.status !== 'success' || updatedAt <= 0 || !isPersistableQueryKey(query.queryKey)) continue;
    if (oldest === 0 || updatedAt < oldest) oldest = updatedAt;
  }
  return oldest;
}

function OfflineBannerContent() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const dataUpdatedAt = useSyncExternalStore(
    (onChange) => queryClient.getQueryCache().subscribe(onChange),
    () => oldestOnScreenDataUpdatedAt(queryClient)
  );

  const dataAge = dataUpdatedAt > 0
    ? formatUtc(new Date(dataUpdatedAt), 'd MMM, HH:mm', { locale: getDateLocale(i18n.language) })
    : null;

  return (
    <div
      role="status"
      data-testid="offline-banner"
      className="flex shrink-0 items-center gap-2 border-b border-amber-300/60 bg-amber-100 px-4 py-1.5 text-xs font-semibold text-warning dark:border-amber-800/60 dark:bg-amber-950/60"
    >
      <WifiOff className="size-3.5 shrink-0" />
      <span className="min-w-0 truncate">
        {dataAge ? t('offline.bannerWithDate', { date: dataAge }) : t('offline.banner')}
      </span>
    </div>
  );
}

/** Thin strip shown while the browser is offline. Mounted once in the layout. */
export function OfflineBanner() {
  const { browserOnline } = useOnlineStatus();
  if (browserOnline) return null;
  return <OfflineBannerContent />;
}
